import React, { useCallback, useRef } from 'react';

import { heicTo, isHeic } from 'heic-to';
import { MdInsertPhoto } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';

import { Button, S } from '@shared/components';
import { intl } from '@shared/localization';

import styles from './AdvRequirementsComments.module.scss';

const IMAGE_MAX_SIZE = 50; // MB

type Props = {
  setPictureUrl: (url: string | Blob) => void;
  disabled: boolean;
};

function RequirementCommentAddPhoto({ setPictureUrl, disabled }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const handleFileInputClick = () => fileInputRef.current?.click();
  const handleAddPhoto = useCallback(
    async event => {
      let file = event.target.files[0];

      if (!file) return;

      const isHeicFormat = await isHeic(file);

      // Check if file is HEIC and convert to JPEG
      if (isHeicFormat) {
        try {
          // For HEIC files, convert to data URL to avoid CSP issues
          file = await heicTo({
            blob: file,
            type: 'image/jpeg',
            quality: 0.8,
          });
        } catch (error) {
          window.alert(error);

          return;
        }
      }

      // For non-HEIC files, check size and use as File object
      if (file.size > IMAGE_MAX_SIZE * 1024 * 1024) {
        const message = intl.formatMessage(
          { id: 'AdvRequirementsComments.fileRestrictionsError' },
          { fileSize: IMAGE_MAX_SIZE },
        );
        window.alert(message);
        return;
      }

      setPictureUrl(file);
    },
    [setPictureUrl],
  );

  const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <>
      <input
        accept="image/jpeg,image/png,image/heic,image/heif"
        className={styles.requirementCommentImageInput}
        onChange={handleAddPhoto}
        onClick={stopPropagation}
        ref={fileInputRef}
        type="file"
      />
      <Button
        className={styles.requirementCommentFormMediaActionsButton}
        disabled={disabled}
        fitText
        ghost
        noBorder
        onClick={handleFileInputClick}
        size="small"
        uppercase={false}
      >
        <MdInsertPhoto />
        <FormattedMessage id="AdvRequirementsComments.addPhoto" />
      </Button>
      <S size="5" inline>
        <FormattedMessage
          id="AdvRequirementsComments.fileRestrictions"
          values={{ fileSize: IMAGE_MAX_SIZE }}
        />
      </S>
    </>
  );
}

export default RequirementCommentAddPhoto;
